// @flow

import React from 'react';
import _ from 'lodash';

import { FormField as Item } from '@zapier/product-get-help';
import { Link } from '@zapier/design-system';

import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';

export const createMediaField = (overrides: {} = {}): Item => ({
  id: 'media',
  type: 'image',
  label: `Please provide a screenshot of the issue if applicable.`,
  details: `Screenshots are the best way to show us exactly what is going on.`,
  ..._.omit(overrides, 'details'),
});

export const taskField = {
  id: 'task',
  type: 'text',
  label: `Please provide a link to the error in your Zap History if it appears there, so we can investigate the error directly.`,
  details: () => (
    <ol>
      <li>
        Find the example in your{' '}
        <Link href="/app/history" target="_blank">
          Zap History
        </Link>
        .
      </li>
      <li>Copy the URL from the specific task.</li>
      <li>Paste the URL below.</li>
    </ol>
  ),
  placeholder: `https://zapier.com/app/history/12345678-12ab-cdef-0000-1a2b3c4d5e6f`,
};

export const messageField = {
  id: 'message',
  type: 'text',
  label: `How can we help you today?`,
  isMultiline: true,
  isRequired: true,
};

export const subjectField = {
  id: 'subject',
  type: 'text',
  label: `Subject`,
};

const userFields = [
  {
    id: 'full_name',
    type: 'text',
    label: 'Your name',
    isRequired: true,
  },
  {
    id: 'email',
    type: 'text',
    inputType: 'email',
    label: 'Your email address',
    isRequired: true,
  },
  {
    ...subjectField,
  },
];

export const isCriticalIssueField = {
  id: 'is_critical_issue',
  type: 'checkbox',
  label: 'Critical Issue',
  details:
    'Check this box only if your issue needs urgent attention because it disrupts your business operations. For example: functionality loss, data loss, or possible system outage.',
  isRequired: false,
  zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.CRITICAL_TICKET,
};

export const criticalIssuePrimaryReasonField = {
  id: 'critical_issue_primary_reason',
  type: 'dropdown',
  label: 'Primary reason this issue is critical',
  items: [
    {
      label: 'Preventing customer transactions',
      value: 'critical_preventing_customer_transactions',
    },
    {
      label: 'Blocking internal workflows',
      value: 'critical_blocking_internal_workflows',
    },
    {
      label: 'Compliance requirements',
      value: 'critical_compliance_requirements',
    },
    {
      label: 'Other essential operations',
      value: 'critical_other_essential_operations',
    },
  ],
  isRequired: true,
  visibilityParentId: 'is_critical_issue',
  zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.CRITICAL_TICKET_PRIMARY_REASON,
};

export const criticalIssueBlockedOutcomeField = {
  id: 'critical_issue_blocked_outcome',
  type: 'text',
  isMultiline: true,
  label: 'Blocked outcome(s)',
  details:
    'Please describe the specific outcome(s) that are currently blocked due to this issue (e.g., onboarding delays, task failures, data not syncing, customer-facing interruptions).',
  isRequired: true,
  visibilityParentId: 'is_critical_issue',
  zendeskCustomFieldId:
    ZENDESK_CUSTOM_FIELD_IDS.CRITICAL_TICKET_BLOCKED_OUTCOME,
};

export const criticalIssueScaleOfImpactField = {
  id: 'critical_issue_scale_of_impact',
  type: 'text',
  isMultiline: true,
  label: 'Scale of impact for this issue',
  details:
    'Please indicate the affected scope (e.g., "impacts all customer onboarding," "affects 3 teams," "blocks 500+ daily tasks"). Please include Zap names or links where applicable.',
  isRequired: true,
  visibilityParentId: 'is_critical_issue',
  zendeskCustomFieldId:
    ZENDESK_CUSTOM_FIELD_IDS.CRITICAL_TICKET_SCALE_OF_IMPACT,
};

export const criticalIssueTroubleshootingDetailsField = {
  id: 'critical_issue_troubleshooting_details',
  type: 'text',
  isMultiline: true,
  label: 'Timeframe and troubleshooting details',
  details:
    'Please describe when the issue began, whether it is ongoing or intermittent, and any internal troubleshooting or temporary workarounds you have attempted.',
  isRequired: true,
  visibilityParentId: 'is_critical_issue',
  zendeskCustomFieldId:
    ZENDESK_CUSTOM_FIELD_IDS.CRITICAL_TICKET_TROUBLESHOOTING_DETAILS,
};

export const getCriticalIssueFields = (isLoggedIn: boolean) => {
  if (!isLoggedIn) {
    return [];
  }

  return [
    isCriticalIssueField,
    criticalIssuePrimaryReasonField,
    criticalIssueBlockedOutcomeField,
    criticalIssueScaleOfImpactField,
    criticalIssueTroubleshootingDetailsField,
  ];
};

export const getUserFields = (isLoggedIn: boolean) =>
  isLoggedIn ? [subjectField] : userFields;

export const getDefaultFields = (isLoggedIn: boolean = true) => [
  ...getUserFields(isLoggedIn),
  messageField,
  ...getCriticalIssueFields(isLoggedIn),
];
